"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  UploadCloud,
  Image as ImageIcon,
  Trash2,
  Plus,
  ZoomIn,
  Check,
  Loader2,
} from "lucide-react";
import toast from "react-hot-toast";
import { PrimaryButton } from "../../components/buttons/primaryButton/PrimaryButton";
import {
  getBooklyProfileImages,
  postBooklyProfileImages,
  deleteBooklyProfileImages,
} from "../../api/profile/profileServices";

/**
 * Сжимает изображение через HTML5 Canvas и возвращает оптимизированный File в формате JPEG
 */
const compressImage = (
  file: File,
  maxWidth = 1920,
  quality = 0.8,
): Promise<File> =>
  new Promise((resolve) => {
    if (!file.type.startsWith("image/")) {
      return resolve(file);
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const scale = img.width > maxWidth ? maxWidth / img.width : 1;
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return resolve(file);
      }

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            return resolve(file);
          }
          const compressedFile = new File(
            [blob],
            file.name.replace(/\.[^/.]+$/, ".jpg"),
            {
              type: "image/jpeg",
              lastModified: Date.now(),
            },
          );
          resolve(compressedFile);
        },
        "image/jpeg",
        quality,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    img.src = objectUrl;
  });

export interface BusinessPhoto {
  id: number;
  image: string;
}

interface LocalPhotoItem {
  id: number | string;
  image: string;
  file?: File;
  isLoading?: boolean;
  isDeleting?: boolean;
}

export interface BusinessPhotosModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialPhotos?: BusinessPhoto[];
}

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/jpg"];

export default function BusinessPhotosModal({
  isOpen,
  onClose,
  initialPhotos,
}: BusinessPhotosModalProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const addMoreInputRef = useRef<HTMLInputElement | null>(null);

  const [photos, setPhotos] = useState<LocalPhotoItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState<LocalPhotoItem | null>(null);

  const isAnyUploading = photos.some((p) => p.isLoading);
  const isAnyDeleting = photos.some((p) => p.isDeleting);
  const isBusy = isAnyUploading || isAnyDeleting || isSaving;

  const fetchPhotos = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await getBooklyProfileImages();
      const rawData: BusinessPhoto[] = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.results)
        ? res.data.results
        : [];
      setPhotos(rawData);
    } catch (err: any) {
      console.error("Failed to load business photos:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initialize photos on open
  useEffect(() => {
    if (!isOpen) return;

    if (initialPhotos && initialPhotos.length > 0) {
      setPhotos(initialPhotos);
      return;
    }

    fetchPhotos();
  }, [isOpen, initialPhotos, fetchPhotos]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (isBusy) return;
        if (previewPhoto) {
          setPreviewPhoto(null);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, previewPhoto, onClose, isBusy]);

  // Process incoming files
  const handleProcessFiles = useCallback(
    (files: FileList | File[]) => {
      const fileList = Array.from(files);
      if (!fileList.length) return;

      let sizeExceeded = false;
      let typeInvalid = false;
      const validPhotos: LocalPhotoItem[] = [];

      fileList.forEach((file) => {
        if (!ALLOWED_TYPES.includes(file.type)) {
          typeInvalid = true;
          return;
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
          sizeExceeded = true;
          return;
        }

        const objectUrl = URL.createObjectURL(file);
        validPhotos.push({
          id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          image: objectUrl,
          file,
          isLoading: true,
        });
      });

      if (typeInvalid) {
        toast.error(
          t(
            "home.fileTypeError",
            "Only image files (JPG, PNG, WEBP) are allowed.",
          ),
        );
      }

      if (sizeExceeded) {
        toast.error(
          t(
            "home.fileSizeError",
            "Some files were skipped because they exceed 10MB.",
          ),
        );
      }

      if (validPhotos.length > 0) {
        setPhotos((prev) => [...prev, ...validPhotos]);

        // Upload each photo immediately with loading state
        validPhotos.forEach(async (photoItem) => {
          if (!photoItem.file) return;
          try {
            const compressedFile = await compressImage(photoItem.file);
            const formData = new FormData();
            formData.append("image", compressedFile, compressedFile.name);
            const res = await postBooklyProfileImages(formData);
            const resData = res?.data?.data || res?.data;
            const serverId = resData?.id;
            const serverImage = resData?.image || resData?.image_url;

            setPhotos((prev) =>
              prev.map((p) =>
                p.id === photoItem.id
                  ? {
                      ...p,
                      id: serverId || p.id,
                      image: serverImage || p.image,
                      isLoading: false,
                      file: undefined,
                    }
                  : p,
              ),
            );
            toast.success(
              t("home.photoUploadedSuccess", "Business photo added successfully"),
            );
          } catch (err: any) {
            console.error("Upload error:", err);
            toast.error(
              err?.response?.data?.message ||
                err?.message ||
                t("home.uploadError", "Failed to upload photo"),
            );
            setPhotos((prev) => prev.filter((p) => p.id !== photoItem.id));
            if (photoItem.image.startsWith("blob:")) {
              try {
                URL.revokeObjectURL(photoItem.image);
              } catch {}
            }
          }
        });
      }
    },
    [t],
  );

  // Drag & drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer?.files) {
      handleProcessFiles(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleProcessFiles(e.target.files);
      e.target.value = "";
    }
  };

  // Delete single photo
  const handleDeletePhoto = async (
    id: number | string,
    e?: React.MouseEvent,
  ) => {
    e?.stopPropagation();

    // Set deleting loading state on target photo
    setPhotos((prev) =>
      prev.map((p) => (p.id === id ? { ...p, isDeleting: true } : p)),
    );

    if (typeof id === "number") {
      try {
        await deleteBooklyProfileImages(id);
        toast.success(
          t("home.photoDeletedSuccess", "Business photo deleted successfully"),
        );
      } catch (err: any) {
        console.error("Delete photo error:", err);
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            t("home.deleteError", "Failed to delete photo"),
        );
        // Reset deleting state on failure
        setPhotos((prev) =>
          prev.map((p) => (p.id === id ? { ...p, isDeleting: false } : p)),
        );
        return;
      }
    }

    setPhotos((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target?.image?.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(target.image);
        } catch {}
      }

      return prev.filter((p) => p.id !== id);
    });

    if (previewPhoto?.id === id) {
      setPreviewPhoto(null);
    }
  };

  // Save / Compress & FormData
  const handleSave = async () => {
    try {
      setIsSaving(true);

      if (isAnyUploading) {
        toast.error(
          t("home.uploadingPhotos", "Uploading photos, please wait"),
        );
        return;
      }

      const pendingPhotos = photos.filter(
        (p): p is LocalPhotoItem & { file: File } =>
          Boolean(p.file && !p.isLoading),
      );

      for (let i = 0; i < pendingPhotos.length; i++) {
        const photo = pendingPhotos[i];
        setPhotos((prev) =>
          prev.map((p) => (p.id === photo.id ? { ...p, isLoading: true } : p)),
        );
        const compressedFile = await compressImage(photo.file);
        const formData = new FormData();
        formData.append("image", compressedFile, compressedFile.name);
        const res = await postBooklyProfileImages(formData);
        const resData = res?.data?.data || res?.data;
        const serverId = resData?.id;
        const serverImage = resData?.image || resData?.image_url;
        setPhotos((prev) =>
          prev.map((p) =>
            p.id === photo.id
              ? {
                  ...p,
                  id: serverId || p.id,
                  image: serverImage || p.image,
                  isLoading: false,
                  file: undefined,
                }
              : p,
          ),
        );
      }

      toast.success(
        t("home.photosSavedSuccess", "Business photos saved successfully!"),
      );
      onClose();
    } catch (err: any) {
      console.error("Save error:", err);
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to save photos",
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <AnimatePresence>
        <motion.div
          key="business-photos-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black/50 z-50 backdrop-blur-xs flex items-center justify-center p-3 sm:p-6"
          onClick={() => !isBusy && onClose()}
        >
          <motion.div
            key="business-photos-modal"
            initial={{ scale: 0.95, opacity: 0, y: 15 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 15 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="bg-white rounded-3xl w-full max-w-3xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-white">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-[#020637]/5 border border-[#020637]/15 flex items-center justify-center text-[#020637]">
                  <ImageIcon className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">
                      {t("home.businessPhotosModalTitle", "Business Photos")}
                    </h2>
                    {photos.length > 0 && (
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-[#020637]/5 text-[#020637] border border-[#020637]/15">
                        {photos.length}
                      </span>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                    {t(
                      "home.businessPhotosModalSubtitle",
                      "Manage and showcase photos of your workspace, services, and team",
                    )}
                  </p>
                </div>
              </div>

              <button
                type="button"
                disabled={isBusy}
                onClick={onClose}
                className={`w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors ${
                  isBusy ? "opacity-40 cursor-not-allowed" : "cursor-pointer"
                }`}
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 no-scrollbar">
              {/* Hidden file inputs */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/jpg"
                onChange={handleFileInputChange}
                className="hidden"
              />
              <input
                ref={addMoreInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/jpg"
                onChange={handleFileInputChange}
                className="hidden"
              />

              {/* Upload Dropzone */}
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !isBusy && fileInputRef.current?.click()}
                className={`
                  relative border-2 border-dashed rounded-2xl p-6 sm:p-8 text-center transition-all duration-200
                  flex flex-col items-center justify-center gap-3
                  ${
                    isDragging
                      ? "border-[#020637] bg-[#020637]/5 scale-[0.99]"
                      : isBusy
                      ? "border-gray-200 bg-gray-50/40 opacity-70 cursor-not-allowed"
                      : "border-gray-200 bg-gray-50/60 hover:bg-gray-50 hover:border-[#020637] cursor-pointer"
                  }
                `}
              >
                <div
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-transform duration-200 ${
                    isDragging
                      ? "bg-[#020637] text-white scale-110"
                      : "bg-white text-[#020637] shadow-xs border border-gray-100"
                  }`}
                >
                  <UploadCloud className="w-6 h-6" />
                </div>

                <div className="space-y-1">
                  <p className="text-sm font-semibold text-gray-800">
                    {t(
                      "home.dragDropPhotosText",
                      "Drag and drop your photos here, or",
                    )}{" "}
                    <span className="text-[#020637] underline font-bold">
                      {t("home.browseFiles", "browse files")}
                    </span>
                  </p>
                  <p className="text-xs text-gray-400">
                    {t(
                      "home.photoUploadHelp",
                      "Supports JPG, PNG, WEBP (up to 10MB each)",
                    )}
                  </p>
                </div>
              </div>

              {/* Photos Gallery */}
              {isLoading ? (
                <div className="py-12 flex flex-col items-center justify-center gap-3 text-gray-400">
                  <Loader2 className="w-8 h-8 animate-spin text-[#020637]" />
                  <p className="text-xs text-gray-500 font-medium">
                    {t("common.loading", "Loading photos...")}
                  </p>
                </div>
              ) : photos.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
                      {t("home.businessPhotos", "Business Photos")} (
                      {photos.length})
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3.5">
                    {photos.map((item, idx) => (
                      <div
                        key={item.id}
                        className="group relative aspect-square rounded-2xl overflow-hidden bg-gray-100 border border-gray-200 hover:border-gray-300 transition-all duration-200 shadow-2xs hover:shadow-md"
                      >
                        {/* Image Thumbnail */}
                        <img
                          src={item.image}
                          alt={`Photo ${idx + 1}`}
                          className="w-full h-full object-cover select-none"
                          loading="lazy"
                        />

                        {/* Top-Right Quick Delete Button */}
                        <button
                          type="button"
                          disabled={item.isLoading || item.isDeleting}
                          onClick={(e) => handleDeletePhoto(item.id, e)}
                          title={t("home.deletePhoto", "Delete photo")}
                          className={`absolute top-2 right-2 z-10 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-150 shadow-xs ${
                            item.isDeleting
                              ? "bg-red-600 text-white opacity-100 cursor-not-allowed"
                              : item.isLoading
                              ? "bg-black/40 text-white opacity-0 pointer-events-none"
                              : "bg-black/60 hover:bg-red-600 text-white opacity-0 group-hover:opacity-100 cursor-pointer"
                          }`}
                        >
                          {item.isDeleting ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5" />
                          )}
                        </button>

                        {/* Loading Overlay when Uploading (Adding) */}
                        {item.isLoading && (
                          <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2 z-20 text-white p-2 text-center select-none">
                            <Loader2 className="w-6 h-6 animate-spin text-white" />
                            <span className="text-[11px] font-medium tracking-wide">
                              {t("home.uploadingPhotos", "Uploading...")}
                            </span>
                          </div>
                        )}

                        {/* Loading Overlay when Deleting */}
                        {item.isDeleting && (
                          <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2 z-20 text-white p-2 text-center select-none">
                            <Loader2 className="w-6 h-6 animate-spin text-white" />
                            <span className="text-[11px] font-medium tracking-wide">
                              {t("home.btnDeleting", "Deleting...")}
                            </span>
                          </div>
                        )}

                        {/* Hover Overlay with Action Buttons */}
                        {!item.isLoading && !item.isDeleting && (
                          <div className="absolute inset-0 bg-black/45 backdrop-blur-[1px] opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex items-center justify-center gap-2 p-2">
                            <button
                              type="button"
                              onClick={() => setPreviewPhoto(item)}
                              title={t("common.preview", "Preview")}
                              className="w-9 h-9 rounded-full bg-white/95 hover:bg-white text-gray-800 flex items-center justify-center transition-transform hover:scale-110 shadow-sm cursor-pointer"
                            >
                              <ZoomIn className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Add More Photos Card in Grid */}
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => addMoreInputRef.current?.click()}
                      className={`aspect-square rounded-2xl border-2 border-dashed border-gray-200 hover:border-[#020637] bg-gray-50/50 hover:bg-[#020637]/8 flex flex-col items-center justify-center gap-2 text-gray-500 hover:text-[#0e1c7a] transition-all group ${
                        isBusy ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                      }`}
                    >
                      <div className="w-9 h-9 rounded-full bg-white border border-gray-200 group-hover:border-[#020637] flex items-center justify-center text-gray-600 group-hover:text-[#0e1c7a] transition-colors shadow-2xs">
                        <Plus className="w-5 h-5" />
                      </div>
                      <span className="text-xs font-semibold">
                        {t("home.addMorePhotos", "Add More")}
                      </span>
                    </button>
                  </div>
                </div>
              ) : (
                /* Empty state */
                <div className="py-8 px-4 text-center flex flex-col items-center justify-center space-y-2 text-gray-400">
                  <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center text-gray-400">
                    <ImageIcon className="w-7 h-7" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-700">
                    {t("home.noPhotosTitle", "No photos yet")}
                  </h3>
                  <p className="text-xs text-gray-400 max-w-sm">
                    {t(
                      "home.noPhotosDesc",
                      "Upload photos of your business to attract more clients and showcase your workspace.",
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/60 flex items-center justify-between gap-3">
              <div className="text-xs text-gray-500 font-medium hidden sm:block">
                {photos.length > 0
                  ? `${photos.length} ${
                      photos.length === 1 ? "photo" : "photos"
                    }`
                  : ""}
              </div>

              <div className="flex items-center justify-end gap-3 w-full sm:w-auto">
                <PrimaryButton
                  onClick={onClose}
                  disabled={isBusy}
                  variant="ghost"
                  label={t("home.cancel", "Cancel")}
                  className="px-5 text-sm"
                />

                <PrimaryButton
                  disabled={isBusy}
                  onClick={handleSave}
                  leftIcon={
                    isBusy ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )
                  }
                  label={
                    isAnyUploading
                      ? t("home.uploadingPhotos", "Uploading...")
                      : isAnyDeleting
                      ? t("home.btnDeleting", "Deleting...")
                      : isSaving
                      ? t("auth.saving", "Saving...")
                      : t("home.savePhotos", "Save Photos")
                  }
                  className="px-6 text-sm"
                />
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Lightbox Preview Modal */}
      <AnimatePresence>
        {previewPhoto && (
          <motion.div
            key="lightbox-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setPreviewPhoto(null)}
          >
            <motion.div
              key="lightbox-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-4xl max-h-[85vh] flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={previewPhoto.image}
                alt="Preview"
                className="max-w-full max-h-[80vh] rounded-2xl object-contain shadow-2xl"
              />

              <button
                type="button"
                onClick={() => setPreviewPhoto(null)}
                className="absolute -top-10 right-0 text-white/80 hover:text-white transition-colors cursor-pointer"
              >
                <X className="w-6 h-6" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
